import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Archive,
  Banknote,
  BanknoteX,
  Calendar,
  Check,
  CirclePlus,
  FileText,
  Grid2X2,
  Loader2,
  Plus,
  TriangleAlert,
  Type,
  X,
} from "lucide-react";
import { Suspense, useMemo, useState } from "react";
import type { SalesTaxTransactionStatus } from "schema";
import {
  categoryDisplayNames,
  formatCurrency,
  getPeriodsFromStartDate,
  type State,
  stateNames,
} from "shared";
import { toast } from "sonner";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderCell } from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { restrictRoute } from "@/lib/routeBlocker";
import type { RouterOutputs } from "@/lib/trpcClient";

export const Route = createFileRoute("/_authed/_org/tax")({
  beforeLoad: async ({ context }) => {
    const { org } = await context.queryClient.ensureQueryData(
      context.trpc.auth.session.queryOptions(),
    );
    if (!org?.salesTaxState) {
      throw redirect({
        to: "/tax-setup",
      });
    }

    restrictRoute(context, {
      redirect: {
        to: "/dashboard",
      },
      requiredRole: "viewOnlyAdmin",
    });
  },
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.tax.listRedemptions.queryOptions());
  },
});

// Before a redemption = to refund, missing, ineligible
// After = refunded, not refunded
type View = SalesTaxTransactionStatus;

type TaxTransaction = RouterOutputs["tax"]["taxTransactions"][number];
const dtf = createColumnConfigHelper<TaxTransaction>();

const columnsConfig = [
  dtf
    .option()
    .id("Status")
    .displayName("Status")
    .accessor((row) => row.taxData.status)
    .transformOptionFn((u) => ({
      label: u,
      value: u,
    }))
    .hide()
    .icon(CirclePlus)
    .build(),
  dtf
    .text()
    .id("Merchant")
    .accessor(
      (row) =>
        row.transaction.merchant?.description ?? row.transaction.merchant?.name,
    )
    .displayName("Merchant")
    .icon(Type)
    .build(),
  dtf
    .date()
    .id("Date")
    .displayName("Date")
    .accessor((row) => row.transaction.originatedAt)
    .icon(Calendar)
    .build(),
  dtf
    .option()
    .id("Category")
    .accessor((row) => row.transaction.category)
    .displayName("Spend Category")
    .icon(Grid2X2)
    .transformOptionFn((u) => {
      return {
        label: categoryDisplayNames[u],
        value: u,
      };
    })
    .build(),
];

// Table columns configuration
const tableColumns: ColumnDef<TaxTransaction>[] = [
  {
    accessorFn: (row) =>
      row.transaction.merchant?.description ?? row.transaction.merchant?.name,
    cell: ({ row: { original } }) => {
      return (
        <Link
          className="flex flex-row items-center hover:underline"
          params={{ id: original.transaction.id }}
          to="/tax/$id"
        >
          <div className="flex flex-col">
            <span>
              {original.transaction.merchant?.description ||
                original.transaction.merchant?.name}
            </span>
            <span className="text-xs">
              {categoryDisplayNames[original.transaction.category]}
            </span>
          </div>
        </Link>
      );
    },

    id: "Merchant",
  },
  {
    accessorFn: (row) => row.transaction.originatedAt,
    cell: ({ row }) => (
      <RenderCell date={row.original.transaction.originatedAt} type="date" />
    ),
    enableResizing: true,
    id: "Date",
  },
  {
    accessorFn: (row) => row.transaction.category,
    cell: () => null,
    id: "Category",
  },
  {
    accessorFn: (row) => row.transaction.amountCents,
    cell: ({ row }) => (
      <RenderCell
        amount={row.original.transaction.amountCents}
        type="currency"
      />
    ),
    id: "Amount",
    meta: {
      align: "right",
    },
  },
  {
    accessorFn: (row) => row.taxData.salesTaxCents,
    cell: ({ row }) => {
      const salesTaxCents = row.original.taxData.salesTaxCents;
      if (salesTaxCents === null || salesTaxCents === undefined) {
        return <span className="text-muted-foreground italic">Missing</span>;
      }
      return <RenderCell amount={salesTaxCents} type="currency" />;
    },
    id: "Sales Tax",
  },
  {
    accessorFn: (row) => row.receipts?.length > 0,
    cell: ({ row }) => {
      const hasReceipt = row.original.receipts?.length > 0;
      return (
        <div className="flex items-center justify-center">
          {hasReceipt ? (
            <div className="flex items-center gap-1 text-green-600">
              <Check className="h-4 w-4" />
              <FileText className="h-4 w-4" />
            </div>
          ) : (
            <div className="flex items-center gap-1 text-red-600">
              <X className="h-4 w-4" />
              <FileText className="h-4 w-4" />
            </div>
          )}
        </div>
      );
    },
    id: "Receipt",
  },
  {
    accessorFn: (row) => row.taxData.status,
    cell: ({ row }) => {
      return <RenderCell status={row.original.taxData.status} type="status" />;
    },
    id: "Status",
  },
];

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();

  const navigate = Route.useNavigate();
  const location = useLocation();

  const [showRefundAlert, setShowRefundAlert] = useState(false);
  const [acknowledgedMissingItems, setAcknowledgedMissingItems] =
    useState(false);

  const {
    data: { org },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());
  if (!org || !org?.salesTaxState) throw new Error("Invalid Organization"); // Should not happen due to route restriction

  const periodRange = org.salesTaxState === "NC" ? "half-year" : "month";

  const periods = getPeriodsFromStartDate(org.createdAt as Date, periodRange);

  // Default to most recent period
  const [period, setPeriod] = useState(periods[0]);

  const { data: trxData, isLoading: isTransactionsLoading } = useQuery(
    trpc.tax.taxTransactions.queryOptions(period),
  );

  const transactions = trxData ?? [];

  const { data: redemptions, isLoading: isRedemptionsLoading } = useQuery(
    trpc.tax.listRedemptions.queryOptions(),
  );

  const isLoading = isTransactionsLoading || isRedemptionsLoading;

  // const { mutate: rollback } = useMutation(
  //   trpc.tax.rollbackRedemption.mutationOptions(),
  // );

  // Mutation for creating refund
  const { mutate: createRefund, isPending: isCreatingRefund } = useMutation(
    trpc.tax.createRedemption.mutationOptions({
      onError: (error) => {
        toast.error(error.message || "Failed to create refund");
      },
      onSuccess: async () => {
        setAcknowledgedMissingItems(false);
        setShowRefundAlert(false);
        // Invalidate queries to refresh data

        queryClient.invalidateQueries(trpc.tax.listRedemptions.queryOptions());
        await queryClient.refetchQueries(
          trpc.tax.taxTransactions.queryOptions(period),
        );
        handleViewTabSelect("redeemed");
        toast.success("Refund created successfully!");
      },
    }),
  );
  const tableName = "tax";

  const columnVisibility = {
    Category: false,
  };

  // Table configuration
  const { table, filters, actions, columns, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data: transactions,
      filterOptions: {
        columnsConfig,
        defaultFilters: [
          {
            columnId: "Status",
            hide: true,
            operator: "is any of",
            type: "option",
            values: ["eligible"],
          },
        ],
        strategy: "client",
      },
      getRowId: (row) => row.transaction.id.toString(),
      initialState: {
        columnVisibility,
      },
      tableName,
    });

  const [view, setView] = useState<View>(
    filters.find((f) => f.columnId === "Status")?.values[0] ?? "eligible",
  );

  const handleViewTabSelect = (v: View) => {
    setView(v);
    setTableFilters((filters) =>
      filters.map((f) =>
        f.columnId === "Status"
          ? {
              ...f,
              hide: true,
              values: [v],
            }
          : f,
      ),
    );
  };

  const currentRedemption = redemptions?.find(
    (r) => r.salesTaxRedemption.to.toISOString() === period.end.toISOString(),
  );
  const currentPeriodRedeemed = currentRedemption !== undefined;
  const currentPeriodHasEnded = period.end <= new Date();

  const { statusCounts, toReimburseCents } = useMemo(() => {
    const statusCounts: Record<View, number> = {
      eligible: 0,
      ineligible: 0,
      "missing items": 0,
      "not redeemed": 0,
      redeemed: 0,
    };
    let toReimburseCents = 0;
    let _totalCents = 0;
    for (const t of transactions) {
      statusCounts[t.taxData.status] += 1;
      if (t.taxData.status === "eligible") {
        toReimburseCents += t.taxData.salesTaxCents ?? 0;
      }
      _totalCents += t.transaction.amountCents;
    }

    return { statusCounts, toReimburseCents };
  }, [transactions]);

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/tax",
          });
        }
      }}
      open={location.pathname !== "/tax" && location.pathname.includes("/tax")}
    >
      <Page
        heading={`${stateNames[org.salesTaxState as State]} Sales Tax Refunds`}
        actions={
          <>
            <Link to="/tax/refunds">
              <Button variant="outline">
                <Archive />
                <span>Refund History</span>
              </Button>
            </Link>

            {!currentPeriodRedeemed && currentPeriodHasEnded && (
              <AlertDialog open={showRefundAlert}>
                <Button onClick={() => setShowRefundAlert(true)}>
                  <Plus />
                  <span>Create Refund</span>
                </Button>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      File Refund For {period.name}{" "}
                    </AlertDialogTitle>
                  </AlertDialogHeader>

                  <div className="space-y-4">
                    {/* Refund breakdown */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-base">
                          Eligible transactions:
                        </span>
                        <span className="text-base">
                          {statusCounts.eligible}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-base">
                          Refund Total:
                        </span>
                        <span className="font-semibold text-base text-green-600">
                          {formatCurrency(toReimburseCents)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-base">
                          Ineligible transactions:
                        </span>
                        <span className="text-base">
                          {statusCounts.ineligible}
                        </span>
                      </div>
                      {statusCounts["missing items"] > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-base italic">
                            Missing items:
                          </span>
                          <span className="text-base">
                            {statusCounts["missing items"]}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Missing items disclaimer */}
                    {statusCounts["missing items"] > 0 && (
                      <div className="rounded border-2 border-amber-500 bg-amber-50 p-4">
                        <div className="mb-2 flex items-center space-x-2">
                          <span className="font-bold text-amber-800 text-lg">
                            {statusCounts["missing items"]} transactions are
                            missing items
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={acknowledgedMissingItems}
                            id="acknowledge-missing"
                            onCheckedChange={(checked) =>
                              setAcknowledgedMissingItems(checked as boolean)
                            }
                            size={"large"}
                          />
                          <label
                            className="text-amber-800 text-sm"
                            htmlFor="acknowledge-missing"
                          >
                            Ignore {statusCounts["missing items"]} transactions
                            in my filing
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  <AlertDialogFooter>
                    <AlertDialogCancel
                      disabled={isCreatingRefund}
                      onClick={() => {
                        setAcknowledgedMissingItems(false);
                        setShowRefundAlert(false);
                      }}
                    >
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      disabled={
                        isCreatingRefund ||
                        (statusCounts["missing items"] > 0 &&
                          !acknowledgedMissingItems)
                      }
                      onClick={() => {
                        createRefund(period);
                      }}
                    >
                      {isCreatingRefund && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      File A Refund
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </>
        }
        tabs={
          <Tabs
            onValueChange={(v) => {
              handleViewTabSelect(v as View);
            }}
            value={view}
            className="@xl/page:w-auto w-full"
          >
            <TabsList
              variant="standard"
              className="h-fit @xl/page:w-auto w-full @xl/page:flex-row flex-col [&>button]:w-full [&>button]:py-2"
            >
              {!currentPeriodRedeemed ? (
                <>
                  <TabsTrigger
                    value="eligible"
                    variant="standard"
                    count={statusCounts.eligible}
                  >
                    <div className="flex items-center gap-2">
                      <Banknote className="size-3.5 text-neutral-400" />
                      To Refund
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="missing items"
                    variant="standard"
                    count={statusCounts["missing items"]}
                  >
                    <div className="flex items-center gap-2">
                      <TriangleAlert className="size-3.5 text-neutral-400" />
                      Missing Items
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="ineligible"
                    variant="standard"
                    count={statusCounts.ineligible}
                  >
                    <div className="flex items-center gap-2">
                      <BanknoteX className="size-3.5 text-neutral-400" />
                      Ineligible
                    </div>
                  </TabsTrigger>
                </>
              ) : (
                <>
                  <TabsTrigger value="redeemed" variant="standard">
                    <div className="flex flex-row items-center">
                      <span>Refunded</span>
                      <div className="ml-2 rounded-full border p-1">
                        {statusCounts.redeemed}
                      </div>
                    </div>
                  </TabsTrigger>

                  <TabsTrigger value="not redeemed" variant="standard">
                    <div className="flex flex-row items-center">
                      <span>Not Refunded</span>
                      <div className="ml-2 rounded-full border p-1">
                        {statusCounts["not redeemed"]}
                      </div>
                    </div>
                  </TabsTrigger>
                </>
              )}
            </TabsList>
          </Tabs>
        }
        table={{
          actions,
          columns,
          filters,
          strategy,
          table,
        }}
        secondaryActions={
          <div className="ml-2 flex items-center gap-4">
            {currentRedemption === undefined ? (
              <div className="@lg/page:block hidden text-nowrap">
                <span className="font-bold text-lg">
                  {isLoading ? "—" : formatCurrency(toReimburseCents)}
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  eligible for refund
                </span>
              </div>
            ) : (
              <div>
                <span className="font-bold text-green-500 text-lg">
                  {formatCurrency(
                    currentRedemption.salesTaxRedemption.redeemedCents,
                  )}
                </span>
                <span className="text-muted-foreground"> refunded</span>
              </div>
            )}

            <Select
              onValueChange={(value) =>
                setPeriod(
                  () => periods.find((p) => p.name === value) ?? periods[0],
                )
              }
              value={period.name}
            >
              <SelectTrigger className="max-w-48">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {periods.map((period) => (
                  <SelectItem
                    key={period.name}
                    onMouseEnter={() => {
                      queryClient.ensureQueryData(
                        trpc.tax.taxTransactions.queryOptions(period),
                      );
                    }}
                    value={period.name}
                  >
                    {period.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Transactions Found</span>
            </div>
          }
          table={table}
          tableName="tax"
          navOptions={(row) =>
            linkOptions({
              params: {
                id: row.original.transaction.id,
              },
              to: "/tax/$id",
            })
          }
          loading={isLoading}
          className="[&_th]:border-t-0!"
        />

        <TablePagination tableName={"transactions"} table={table} />
      </Page>
      <SheetContent
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        size={location.pathname === "/tax/refunds" ? "default" : "large"}
      >
        <Suspense fallback={<SheetSkeleton />}>
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}
